/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import {
  CheckoutData,
  Cart,
  TableCheckout,
  StaffCheckout,
  StaffCheckoutIntent,
} from "@lemmonhq/types";
import { CartPaymentEditor } from "./CartPaymentEditor";
import { TableCheckoutEditor } from "./TableCheckoutEditor";

export type CheckoutEditorProps = {
  isOpen: boolean;
  checkoutData: CheckoutData;
  onClose: () => void;
  onUpdate: (checkoutData: CheckoutData) => void;
  /** When true, render inline (no RightSheet) — used for the tablet dock. */
  embedded?: boolean;
};

export const CheckoutEditor = defineComponent<CheckoutEditorProps>({
  name: "CheckoutEditor",
  props: ["isOpen", "checkoutData", "onClose", "onUpdate", "embedded"],
  setup(props) {
    const { t } = useLocalization();

    const payloadType = computed(() => props.checkoutData.payload?.type);

    const titleText = computed(() => t("checkout.editor.split_partial"));

    const titleIcon = computed(() =>
      payloadType.value === "cart" ? "call_split" : "table_restaurant",
    );

    // Carries no padding of its own: both RightSheet headers already pad, and
    // on mobile this sits directly to the right of the sheet's back button.
    const title = computed(() => (
      <div class="flex min-w-0 items-center gap-2 text-accent">
        <span class="material-symbols-outlined text-xl text-primary">
          {titleIcon.value}
        </span>
        <span class="truncate text-base font-semibold sm:text-lg">
          {titleText.value}
        </span>
      </div>
    ));

    // Patch a field on the checkout's staff_checkout lifecycle (which exists by
    // the time this editor is open for a cart payload).
    const patchStaffCheckout = (patch: Partial<StaffCheckout>) => {
      const sc = props.checkoutData.staffCheckout;
      if (!sc) return;
      props.onUpdate({
        ...props.checkoutData,
        staffCheckout: { ...sc, ...patch },
      });
    };

    // Applying a leg intent: always publish it; in sheet mode also close so the
    // main Pay buttons take over. In embedded (dock) mode the panel stays open
    // for the next leg.
    const applyIntent = (intent: StaffCheckoutIntent | undefined) => {
      patchStaffCheckout({ intent });
      if (!props.embedded) props.onClose();
    };

    const body = () => (
      <div class="flex flex-1 min-h-0 flex-col gap-4 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        {payloadType.value === "cart" && (
          <CartPaymentEditor
            key={(props.checkoutData.staffCheckout?.payments || []).length}
            cart={props.checkoutData.payload!.object as Cart}
            staffCheckoutPayments={
              props.checkoutData.staffCheckout?.payments || []
            }
            lockedMode={props.checkoutData.staffCheckout?.mode}
            selection={props.checkoutData.staffCheckout?.selection}
            onSelectionChange={(selection) =>
              patchStaffCheckout({ selection })
            }
            autoApply={props.embedded}
            onApply={applyIntent}
          />
        )}

        {payloadType.value === "table" && (
          <TableCheckoutEditor
            tableCheckout={props.checkoutData.payload!.object as TableCheckout}
            onChange={(tableCheckout) => {
              props.onUpdate({
                ...props.checkoutData,
                payload: { type: "table", object: tableCheckout },
              });
              props.onClose();
            }}
          />
        )}
      </div>
    );

    return () => {
      if (props.embedded) {
        // Inline dock (tablet two-column). No sheet chrome. Full height, with a
        // small top gap so the sheet's back button stays clear.
        return (
          <div class="flex h-full flex-col pt-3">
            <div class="flex items-center gap-2 border-b border-base-content/10 px-4 py-3 text-accent hidden">
              <span class="material-symbols-outlined text-xl text-primary">
                {titleIcon.value}
              </span>
              <span class="text-base font-semibold">{titleText.value}</span>
            </div>
            <div class="flex-1 min-h-0 flex flex-col overflow-hidden">
              {body()}
            </div>
          </div>
        );
      }
      return (
        <RightSheet
          isOpen={props.isOpen}
          onClose={props.onClose}
          title={title.value}
          width="640px"
          // Phones get the full screen: this editor is a task in its own right
          // (pick items, split, collect a leg) and a bottom sheet left the item
          // list and the Paying panel fighting over a fraction of the viewport.
          mobileVariant="full-screen"
          // Once a leg is collected the cashier finishes the remaining legs
          // from here; backing out mid-split is what we want to avoid.
          hideBack={
            (props.checkoutData.staffCheckout?.payments || []).length > 0
          }
        >
          {body()}
        </RightSheet>
      );
    };
  },
});
